import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/pro-solid-svg-icons';
import {
  selectSearch,
  selectPageNumber,
  selectPageSize,
  selectInstitutions,
  selectInstitutionsTotal,
} from '../../../../ReduxStorage/Wasabi/institutions/selectors';

import {
  ROUTE_WASABI_INSTITUTION_LIST,
  ROUTE_WASABI_INSTITUTION_INFO,
} from '../../../../ReduxStorage/routeActions';
import { Paginate } from '../../../components/Pagination/Paginate';
import { DebouncedInputField } from '../../../components/Input/DebouncedInputField/component';
import { capitalizeFirstLetter } from '../../../utilities/utilities';
import { Link } from '../../Link';

import './InstitutionListPage.scss';
import { selectPortalsByPath } from '../../../../ReduxStorage/Portals/selectors';

export const PortalInstitutionPropType = ImmutablePropTypes.mapContains({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  hasPortal: PropTypes.bool.isRequired,
  privacy: PropTypes.string.isRequired,
  isOnline: PropTypes.bool.isRequired,
  courses: PropTypes.number.isRequired,
});

const PortalPropType = ImmutablePropTypes.mapContains({
  lowCost: PropTypes.bool,
  externalLogInLink: PropTypes.string,
  externalSignUpLink: PropTypes.string,
});

const InstitutionListRow = ({ institution, portals }) => {
  const hasPortal = institution.get('hasPortal');
  const privacy = institution.get('privacy');
  const onlineStatus = institution.get('isOnline') ? 'online' : 'offline';
  const lowCostEnabled =
    hasPortal && portals.getIn([institution.get('id'), 'lowCost']);

  return (
    <tr key={institution.get('id')}>
      <td>
        <Link
          to={{
            type: ROUTE_WASABI_INSTITUTION_INFO,
            params: { institutionId: institution.get('path') },
          }}
        >
          {institution.get('name')}
        </Link>
      </td>
      <td>{institution.get('path')}</td>
      <td>{hasPortal ? 'Portal' : 'Legacy'}</td>
      <td className={`privacy ${privacy}`}>{capitalizeFirstLetter(privacy)}</td>
      <td className={onlineStatus}>{capitalizeFirstLetter(onlineStatus)}</td>
      <td>
        {lowCostEnabled ? (
          <FontAwesomeIcon icon={faCheck} className="check-mark" />
        ) : (
          <FontAwesomeIcon icon={faTimes} className="times-icon" />
        )}
      </td>
      <td>{institution.get('courses')}</td>
    </tr>
  );
};
InstitutionListRow.propTypes = {
  institution: PortalInstitutionPropType.isRequired,
  portals: PortalPropType.isRequired,
};

class _InstitutionListPage extends React.PureComponent {
  static propTypes = {
    institutions: ImmutablePropTypes.listOf(PortalInstitutionPropType)
      .isRequired,
    search: PropTypes.string.isRequired,
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    onUpdateQuery: PropTypes.func.isRequired,
    portals: ImmutablePropTypes.mapOf(PortalPropType).isRequired,
  };

  onPageChange = (page) => {
    this.updateRoute({ page });
  };

  onPageSizeChange = (pageSize) => {
    this.updateRoute({ pageSize });
  };

  updateRoute = (updates) => {
    const { search, page, pageSize, onUpdateQuery } = this.props;

    onUpdateQuery({
      search,
      page,
      pageSize,
      ...updates,
    });
  };

  onChangeFilterBySearchTerm = (search) => {
    this.updateRoute({ search, page: 1 });
  };

  render() {
    const { institutions, search, page, pageSize, total, portals } = this.props;

    const totalPages =
      institutions.size > pageSize ? 1 : Math.ceil(total / pageSize);

    return (
      <institution-list>
        <div className="filters">
          <div className="filters-row">
            <div
              className={classNames('filter-item', 'filter-item-institution')}
            >
              <DebouncedInputField
                label="Institution"
                name="search"
                value={search || ''}
                placeholder="Search Institution"
                onChange={this.onChangeFilterBySearchTerm}
              />
            </div>
          </div>
        </div>
        <table className="results">
          <thead>
            <tr>
              <th>Name</th>
              <th>Path</th>
              <th>Type</th>
              <th>Privacy</th>
              <th>Status</th>
              <th>Low cost flag</th>
              <th># Courses</th>
            </tr>
          </thead>
          <tbody>
            {institutions.size === 0 && (
              <tr>
                <td colSpan={6}>No results found</td>
              </tr>
            )}
            {institutions.map((institution) => (
              <InstitutionListRow
                key={institution.get('id')}
                {...{ institution, portals }}
              />
            ))}
          </tbody>
        </table>
        <Paginate
          {...{ page, pageSize, totalPages }}
          totalResults={total}
          showPageSizeSelector
          handlePageChange={this.onPageChange}
          handlePageSizeChange={this.onPageSizeChange}
        />
      </institution-list>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    institutions: selectInstitutions(state),
    search: selectSearch(state),
    pageSize: selectPageSize(state),
    page: selectPageNumber(state),
    total: selectInstitutionsTotal(state),
    portals: selectPortalsByPath(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onUpdateQuery: (query) =>
      dispatch({
        type: ROUTE_WASABI_INSTITUTION_LIST,
        query,
      }),
  };
};

export const InstitutionListPage = connect(
  mapStateToProps,
  mapDispatchToProps
)(_InstitutionListPage);
